//----------------CORE-----------------//
// Scheduled Reports layout, on the Fleet Reports pattern: a LOCAL route table (the tile and the pages hang
// on the licence-gated configuration and the Solution Features master, not on a role module, so appPaths
// has no entries for them), the configuration read above the <Switch>, per-route gates.
//
// The real boundary is reportsMS; these gates are cosmetic. Never default to "available" before the
// configuration loads, and never redirect before it and the role have loaded: an e-mail deep link to
// /scheduledReports/history/<id> must survive a cold load.
import React from "react";
import { Switch, Route, Redirect, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
//----------------MUI-----------------//
import { makeStyles } from "@mui/styles";
import { Card } from "@mui/material";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import KeyboardReturnOutlinedIcon from "@mui/icons-material/KeyboardReturnOutlined";
//----------------EXTERNAL COMPONENTS-----------------//
import Navbar from "components/Navbars/Navbar.js";
import Sidebar from "components/Sidebar/Sidebar.js";
import Loader from "components/Progress";
import useScheduledReportsConfig from "hooks/useScheduledReportsConfig";
import { reportsLevel } from "utils/reportDefinition";
import List from "components/Scheduled Reports/List";
import Editor from "components/Scheduled Reports/Editor";
import History from "components/Scheduled Reports/History";

const useStyles = makeStyles({
  root: { backgroundColor: "#eeeeee" },
  component: {
    backgroundColor: "#eeeeee",
    position: "absolute",
    top: "64px",
    left: "60px",
    padding: "20px 15px 20px 15px",
    minHeight: "calc(100vh - 64px)",
    width: "calc(100vw - 60px)",
    boxSizing: "border-box",
  },
  centre: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "calc(100vh - 220px)",
  },
});

function Message({ text }) {
  return (
    <Card style={{ padding: "40px 20px", textAlign: "center" }}>
      <p style={{ fontSize: "15px", color: "#c7c7c7", margin: 0 }}>{text}</p>
    </Card>
  );
}

// A wrong answer from a service must not blank the whole app.
class ErrorBoundary extends React.Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? <Message text="This page could not be shown. Please reload the page." /> : this.props.children;
  }
}

export default function ScheduledReportsLayout(props) {
  const classes = useStyles();
  const metaDataValue = useSelector((state) => state.metaData);
  const location = useLocation();
  const { outcome, config } = useScheduledReportsConfig();
  const level = reportsLevel(metaDataValue);
  const full = level === "ALL";
  const roleLoaded = Array.isArray(metaDataValue.apps);

  const backButton = {
    name: "Back",
    path: window.localStorage.getItem("currentService") || "catalogue",
    layout: "/solutions/",
    icon: KeyboardReturnOutlinedIcon,
  };

  const usable = outcome === "available" && roleLoaded && !!level;
  const reportRoutes = usable
    ? [
        { name: "Reports", path: "list", layout: "/scheduledReports/", icon: ListAltIcon },
        ...(full ? [{ name: "New report", path: "new", layout: "/scheduledReports/", icon: AddCircleOutlineIcon }] : []),
      ]
    : [];

  const body = () => {
    if (outcome === "loading" || !roleLoaded) {
      return (
        <div className={classes.centre}>
          <Loader />
        </div>
      );
    }
    if (outcome === "paused") return <Message text="Scheduled Reports is paused, try again later." />;
    if (outcome === "error") return <Message text="Please reload the page." />;
    if (!usable) return <Message text="Not available here." />;
    return (
      <ErrorBoundary>
        <Switch>
          <Route path="/scheduledReports/list">
            <List full={full} />
          </Route>
          <Route path="/scheduledReports/new">
            {full ? <Editor key={location.pathname} config={config} /> : <Redirect to="/scheduledReports/list" />}
          </Route>
          <Route path="/scheduledReports/edit/:id">
            {full ? <Editor key={location.pathname} config={config} /> : <Redirect to="/scheduledReports/list" />}
          </Route>
          <Route path="/scheduledReports/history/:id">
            <History config={config} />
          </Route>
          <Redirect to="/scheduledReports/list" />
        </Switch>
      </ErrorBoundary>
    );
  };

  return (
    <div className={classes.root}>
      <Navbar
        routes={[...(metaDataValue.appPaths || []), { name: "Scheduled Reports", path: "", layout: "/scheduledReports" }]}
        history={props.history}
        services={metaDataValue.services}
      />
      {/* The shared Sidebar reads its highlight from the URL once, at mount: the key re-reads it on navigation. */}
      <Sidebar key={location.pathname} routes={[backButton, ...reportRoutes]} />
      <div className={classes.component}>{body()}</div>
    </div>
  );
}
